import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
} from '@mui/material';
import {useSeedPageCount} from '../../../hooks/productions/useProductions';
import {SeedPageCountRequest} from '../../../api/types/production.type';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import ArrButton from '../../../components/common/arr-button/ArrButton';
import '../Demo.scss';

const SeedPageCountApiTest: React.FC = () => {
  const [pagecount, setPagecount] = useState<number>(1);
  const [seedFilePath, setSeedFilePath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\aurorareview_dev\\destinationlocation\\Automation\\E15BEB11-E73C-4F7A-B506-AB01A6D1CC47\\6EF63FE0-09B0-47DC-B0EB-2A7662DDD9C8\\SeedFiles\\28.PDF'
  );
  const [newSeedFilePath, setNewSeedFilePath] = useState<string>(
    '\\\\devnas10.consiliotest.com\\aurorareview_dev\\DocumentUploads\\rep28.PDF'
  );
  const [documentID, setDocumentID] = useState<number>(28);
  const [productionID, setProductionID] = useState<number>(54);
  const [queryParam, setQueryParam] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const seedPageCountMutation = useSeedPageCount(
    (data) => {
      console.log('Seed page count saved successfully:', data);
    },
    (error) => {
      console.error('Error saving seed page count:', error);
    }
  );

  const handleTestApi = () => {
    const payload: SeedPageCountRequest = {
      pagecount,
      seedFilePath,
      newSeedFilePath,
      documentID,
      productionID,
    };
    seedPageCountMutation.mutate({payload, queryParam: queryParam || undefined});
  };

  const handleReset = () => {
    setPagecount(1);
    setSeedFilePath(
      '\\\\devnas10.consiliotest.com\\aurorareview_dev\\destinationlocation\\Automation\\E15BEB11-E73C-4F7A-B506-AB01A6D1CC47\\6EF63FE0-09B0-47DC-B0EB-2A7662DDD9C8\\SeedFiles\\28.PDF'
    );
    setNewSeedFilePath(
      '\\\\devnas10.consiliotest.com\\aurorareview_dev\\DocumentUploads\\rep28.PDF'
    );
    setDocumentID(28);
    setProductionID(54);
    setQueryParam('');
  };

  const handleCopyJson = async () => {
    const payload = {
      pagecount,
      seedFilePath,
      newSeedFilePath,
      documentID,
      productionID,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyResponse = async () => {
    if (seedPageCountMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(seedPageCountMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        📄 Seed Page Count API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Save seed page count and update seed file path for a document in a
        production
      </Typography>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" data-testid={`seed-page-count-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Seed Page Count
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/seed-page-count
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Save seed page count and update seed file path. Returns success
            status and message.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Payload */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Payload
        </Typography>

        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginTop: 2}}>
          <TextField
            label="Page Count"
            type="number"
            value={pagecount}
            onChange={(e) => setPagecount(Number(e.target.value))}
            size="small"
            placeholder="Enter page count"
            sx={{maxWidth: '200px'}}
          />
          <TextField
            label="Seed File Path"
            type="text"
            value={seedFilePath}
            onChange={(e) => setSeedFilePath(e.target.value)}
            size="small"
            placeholder="Enter seed file path"
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            label="New Seed File Path"
            type="text"
            value={newSeedFilePath}
            onChange={(e) => setNewSeedFilePath(e.target.value)}
            size="small"
            placeholder="Enter new seed file path"
            fullWidth
            multiline
            rows={3}
          />
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            <TextField
              label="Document ID"
              type="number"
              value={documentID}
              onChange={(e) => setDocumentID(Number(e.target.value))}
              size="small"
              placeholder="Enter document ID"
              sx={{minWidth: '200px'}}
            />
            <TextField
              label="Production ID"
              type="number"
              value={productionID}
              onChange={(e) => setProductionID(Number(e.target.value))}
              size="small"
              placeholder="Enter production ID"
              sx={{minWidth: '200px'}}
            />
          </Box>
          <TextField
            label="Query Parameter (Optional)"
            type="text"
            value={queryParam}
            onChange={(e) => setQueryParam(e.target.value)}
            size="small"
            placeholder="Leave empty or enter query param name"
            helperText="Optional query parameter name (e.g., 'param')"
            sx={{maxWidth: '400px'}}
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestApi}
            disabled={seedPageCountMutation.isPending}
          >
            {seedPageCountMutation.isPending
              ? '⏳ Saving...'
              : '🚀 Save Seed Page Count'}
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            🔄 Reset
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {seedPageCountMutation.isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Saving seed page count... Making API request...
          </Typography>
          <LinearProgress sx={{marginTop: 2}} />
        </Box>
      )}

      {/* Error State */}
      {seedPageCountMutation.error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {seedPageCountMutation.error.message}
          </Typography>
        </Box>
      )}

      {/* Success State - Display Response */}
      {seedPageCountMutation.data &&
        !seedPageCountMutation.isPending && (
          <Box>
            <Typography
              variant="h6"
              className="typo-text subhead2_size"
              gutterBottom
            >
              ✅ API Response
            </Typography>

            {/* Success Message */}
            <Box
              sx={{
                padding: 2,
                backgroundColor: '#e8f5e9',
                borderRadius: 1,
                marginBottom: 2,
                border: '1px solid #4caf50',
              }}
            >
              <Typography className="typo-text label2_size" gutterBottom>
                🎉 Seed Page Count Saved Successfully!
              </Typography>
              <Typography className="typo-text body3_size" gutterBottom>
                <strong>Success:</strong>{' '}
                {seedPageCountMutation.data.success ? '✅ Yes' : '❌ No'}
              </Typography>
              <Typography className="typo-text body3_size">
                <strong>Message:</strong>{' '}
                {seedPageCountMutation.data.message}
              </Typography>
            </Box>

            {/* Expandable Sections */}
            <Box sx={{marginTop: 2}}>
              {/* Request Payload */}
              <Accordion>
                <AccordionSummary expandIcon={<CaretDown />}>
                  <Typography className="typo-text label2_size">
                    📤 Request Payload
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{marginBottom: 2}}>
                    <ArrButton
                      variant={copySuccess ? 'secondary' : 'primary'}
                      size="small"
                      onClick={handleCopyJson}
                    >
                      {copySuccess ? '✅ Copied!' : '📋 Copy Payload'}
                    </ArrButton>
                  </Box>
                  <pre
                    style={{
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: '300px',
                    }}
                  >
                    {JSON.stringify(
                      {
                        pagecount,
                        seedFilePath,
                        newSeedFilePath,
                        documentID,
                        productionID,
                      },
                      null,
                      2
                    )}
                  </pre>
                </AccordionDetails>
              </Accordion>

              {/* Response Data */}
              <Accordion>
                <AccordionSummary expandIcon={<CaretDown />}>
                  <Typography className="typo-text label2_size">
                    📥 Response Data
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{marginBottom: 2}}>
                    <ArrButton
                      variant={copySuccess ? 'secondary' : 'primary'}
                      size="small"
                      onClick={handleCopyResponse}
                    >
                      {copySuccess ? '✅ Copied!' : '📋 Copy Response'}
                    </ArrButton>
                  </Box>
                  <pre
                    style={{
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: '300px',
                    }}
                  >
                    {JSON.stringify(seedPageCountMutation.data, null, 2)}
                  </pre>
                </AccordionDetails>
              </Accordion>

              {/* Full Response */}
              <Accordion>
                <AccordionSummary expandIcon={<CaretDown />}>
                  <Typography className="typo-text label2_size">
                    📄 Full JSON Response
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      padding: 2,
                      backgroundColor: '#263238',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: '500px',
                    }}
                  >
                    <pre
                      style={{
                        color: '#4caf50',
                        fontSize: '12px',
                        margin: 0,
                        fontFamily: 'monospace',
                      }}
                    >
                      {JSON.stringify(seedPageCountMutation.data, null, 2)}
                    </pre>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
        )}

      {/* Initial State */}
      {!seedPageCountMutation.isPending &&
        !seedPageCountMutation.data &&
        !seedPageCountMutation.error && (
          <Box
            sx={{
              padding: 3,
              backgroundColor: '#fff3e0',
              borderRadius: 1,
              border: '1px solid #ff9800',
            }}
          >
            <Typography className="typo-text body2_size">
              👆 Fill in the form and click "Save Seed Page Count" button to
              make the API request
            </Typography>
          </Box>
        )}

      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong>{' '}
          /productions/api/v1/production/seed-page-count
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> POST
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Content-Type:</strong> application/json
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Request Body:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {JSON.stringify(
            {
              pagecount,
              seedFilePath,
              newSeedFilePath,
              documentID,
              productionID,
            },
            null,
            2
          )}
        </pre>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Expected Response:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#e8f5e8',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`{
  "success": true,
  "message": "Seed PDF successfully saved"
}`}
        </pre>
      </Box>

      {/* Hook Usage Example */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#f0f8ff'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          💻 Hook Usage Example
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {`import { useSeedPageCount } from '../../../hooks/productions/useProductions';
import { SeedPageCountRequest } from '../../../api/types/production.type';

const MyComponent = () => {
  const seedPageCount = useSeedPageCount(
    (data) => {
      console.log('Success:', data.message);
    },
    (error) => console.error('Error:', error)
  );

  const handleSave = () => {
    const payload: SeedPageCountRequest = {
      pagecount: 1,
      seedFilePath: '\\\\server\\path\\to\\file.PDF',
      newSeedFilePath: '\\\\server\\path\\to\\newfile.PDF',
      documentID: 28,
      productionID: 54
    };
    
    seedPageCount.mutate({ payload });
  };

  return (
    <button 
      onClick={handleSave}
      disabled={seedPageCount.isPending}
    >
      {seedPageCount.isPending ? 'Saving...' : 'Save Seed Page Count'}
    </button>
  );
};`}
        </pre>
      </Box>
    </Box>
  );
};

export default SeedPageCountApiTest;

